import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Plus,
    ArrowUp,
    ArrowDown,
    X,
    Scissors,
    Link2,
    Factory,
    Wrench,
    Clock,
    Coins,
    Users,
    CheckCircle2,
    Calendar,
    Truck,
    Package,
    TimerReset,
    ChevronDown,
} from 'lucide-react';
import {
    ConveyorStationConfig,
    ConveyorStationWithCost,
    Equipment,
    LaborRate,
    ProcessTemplate,
    RFQProgramParams,
    StationEquipmentLine,
    StationEquipmentLineCost,
    StationSetupLaborCost,
    StationSetupLaborEntry,
} from '../types';
import { rfqApi, inventoryApi, processTemplatesApi } from '../services/api';
import { getIsAdmin } from '../utils/auth';
import { computeStationCosts, formatCurrency } from '../utils/costing';
import CycleGantt from './CycleGantt';

interface ConveyorStationsConfigProps {
    rfqId: string;
    rfqTitle?: string;
}

const STATION_HUES = ['#e0461e', '#1e5f8f', '#4a6b3d', '#8a5a2b', '#6a4a7a'];

const DASH = '—';

const formatTakt = (seconds: number | null | undefined): string => {
    if (seconds == null || seconds <= 0) return DASH;
    return `${Number.isInteger(seconds) ? seconds : seconds.toFixed(1)}s`;
};
const formatTaktRate = (seconds: number | null | undefined): string => {
    if (seconds == null || seconds <= 0) return DASH;
    return `${(3600 / seconds).toFixed(1)} jph`;
};
const formatInt = (n: number | null | undefined, suffix = ''): string => {
    if (n == null) return DASH;
    return `${n.toLocaleString()}${suffix}`;
};
const formatText = (v: string | null | undefined): string => {
    return v && v.trim() ? v : DASH;
};

const opCode = (globalIdx: number) =>
    `OP-${String(globalIdx + 1).padStart(2, '0')}`;

const toEditableConfig = (s: ConveyorStationWithCost): ConveyorStationConfig => ({
    station_type_id: s.station_type_id,
    name: s.name,
    comments: s.comments,
    steps: [...s.steps],
    equipment: s.equipment.map((line) => ({
        equipment_id: line.equipment_id,
        quantity: line.quantity,
        is_free_issued: line.is_free_issued,
        from_process_template_id: line.from_process_template_id ?? null,
    })),
    setup_labor: s.setup_labor.map((entry) => ({
        role: entry.role,
        hours: entry.hours,
        worker_type: entry.worker_type,
        from_process_template_id: entry.from_process_template_id ?? null,
    })),
});

interface FlatOp {
    sIdx: number;
    stepIdx: number;
    id: string;
    text: string;
    durationSeconds: number;
    globalIdx: number;
}

const ConveyorStationsConfig: React.FC<ConveyorStationsConfigProps> = ({
    rfqId,
    rfqTitle,
}) => {
    const isAdmin = getIsAdmin();
    const [equipmentCatalog, setEquipmentCatalog] = useState<Equipment[]>([]);
    const [laborRates, setLaborRates] = useState<LaborRate[]>([]);
    const [processTemplates, setProcessTemplates] = useState<ProcessTemplate[]>([]);
    const [stations, setStations] = useState<ConveyorStationConfig[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addingToIdx, setAddingToIdx] = useState<number | null>(null);
    const [addingProcessToIdx, setAddingProcessToIdx] = useState<number | null>(null);
    const [params, setParams] = useState<RFQProgramParams>({});
    const [paramsError, setParamsError] = useState<string | null>(null);
    const paramsRef = useRef<RFQProgramParams>({});

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [list, catalog, rates, templates, loadedParams] = await Promise.all([
                rfqApi.getConveyorStations(rfqId),
                inventoryApi.listEquipment(),
                inventoryApi.listLaborRates(),
                processTemplatesApi.list(),
                rfqApi.getProgramParams(rfqId),
            ]);
            setStations(list.map(toEditableConfig));
            setEquipmentCatalog(catalog);
            setLaborRates(rates);
            setProcessTemplates(templates);
            setParams(loadedParams);
            paramsRef.current = loadedParams;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load conveyor stations');
        } finally {
            setIsLoading(false);
        }
    }, [rfqId]);

    const persistParams = useCallback(
        async (patch: Partial<RFQProgramParams>) => {
            const next: RFQProgramParams = { ...paramsRef.current, ...patch };
            paramsRef.current = next;
            setParams(next);
            if (!isAdmin) return;
            setParamsError(null);
            try {
                const saved = await rfqApi.updateProgramParams(rfqId, next);
                paramsRef.current = saved;
                setParams(saved);
            } catch (err) {
                setParamsError(
                    err instanceof Error ? err.message : 'Failed to save program params'
                );
            }
        },
        [rfqId, isAdmin]
    );

    useEffect(() => {
        void loadData();
    }, [loadData]);

    const equipmentById = useMemo(
        () => new Map(equipmentCatalog.map((e) => [e.id, e])),
        [equipmentCatalog]
    );

    const templateById = useMemo(
        () => new Map(processTemplates.map((t) => [t.id, t])),
        [processTemplates]
    );

    const costed: ConveyorStationWithCost[] = useMemo(
        () => computeStationCosts(stations, equipmentCatalog, laborRates),
        [stations, equipmentCatalog, laborRates]
    );

    const totals = useMemo(() => {
        const goods = costed.reduce((acc, s) => acc + s.total_goods, 0);
        const labor = costed.reduce((acc, s) => acc + s.total_labor, 0);
        const eqCount = costed.reduce((acc, s) => acc + s.equipment.length, 0);
        return { goods, labor, eqCount, total: goods + labor };
    }, [costed]);

    const flatSteps: FlatOp[] = useMemo(() => {
        const out: FlatOp[] = [];
        let g = 0;
        stations.forEach((s, sIdx) => {
            (s.steps ?? []).forEach((step, stepIdx) => {
                out.push({
                    sIdx,
                    stepIdx,
                    id: step.id,
                    text: step.text,
                    durationSeconds: step.duration_seconds,
                    globalIdx: g,
                });
                g += 1;
            });
        });
        return out;
    }, [stations]);

    const moveStepPrev = (sIdx: number, stepIdx: number) => {
        if (sIdx === 0 || stepIdx !== 0) return;
        setStations((prev) => {
            const next = prev.map((s) => ({ ...s, steps: [...(s.steps ?? [])] }));
            const step = next[sIdx].steps!.shift();
            if (step === undefined) return prev;
            next[sIdx - 1].steps!.push(step);
            return next.filter(
                (s) =>
                    (s.steps ?? []).length > 0 ||
                    (s.equipment ?? []).length > 0 ||
                    (s.setup_labor ?? []).length > 0
            );
        });
    };

    const moveStepNext = (sIdx: number, stepIdx: number) => {
        setStations((prev) => {
            if (sIdx >= prev.length - 1) return prev;
            if (stepIdx !== (prev[sIdx].steps ?? []).length - 1) return prev;
            const next = prev.map((s) => ({ ...s, steps: [...(s.steps ?? [])] }));
            const step = next[sIdx].steps!.pop();
            if (step === undefined) return prev;
            next[sIdx + 1].steps!.unshift(step);
            return next.filter(
                (s) =>
                    (s.steps ?? []).length > 0 ||
                    (s.equipment ?? []).length > 0 ||
                    (s.setup_labor ?? []).length > 0
            );
        });
    };

    const splitAfter = (sIdx: number, stepIdx: number) => {
        setStations((prev) => {
            const src = prev[sIdx];
            const steps = [...(src.steps ?? [])];
            if (stepIdx >= steps.length - 1) return prev;
            const tail = steps.splice(stepIdx + 1);
            const newStation: ConveyorStationConfig = {
                station_type_id: src.station_type_id,
                name: 'New station',
                steps: tail,
                equipment: [],
                setup_labor: [],
            };
            const next = prev.map((s, i) => (i === sIdx ? { ...s, steps } : s));
            next.splice(sIdx + 1, 0, newStation);
            return next;
        });
    };

    const mergeWithNext = (sIdx: number) => {
        setStations((prev) => {
            if (sIdx >= prev.length - 1) return prev;
            const a = prev[sIdx];
            const b = prev[sIdx + 1];
            const merged: ConveyorStationConfig = {
                ...a,
                steps: [...(a.steps ?? []), ...(b.steps ?? [])],
                equipment: [...(a.equipment ?? []), ...(b.equipment ?? [])],
                setup_labor: [...(a.setup_labor ?? []), ...(b.setup_labor ?? [])],
            };
            const next = [...prev];
            next.splice(sIdx, 2, merged);
            return next;
        });
    };

    const setStepDuration = (sIdx: number, stepIdx: number, seconds: number) => {
        const safe = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          steps: (s.steps ?? []).map((step, j) =>
                              j === stepIdx
                                  ? { ...step, duration_seconds: safe }
                                  : step
                          ),
                      }
            )
        );
    };

    const renameStation = (sIdx: number, name: string) => {
        setStations((prev) =>
            prev.map((s, i) => (i === sIdx ? { ...s, name: name || undefined } : s))
        );
    };

    const addEquipment = (sIdx: number, item: Equipment) => {
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          equipment: [
                              ...(s.equipment ?? []),
                              {
                                  equipment_id: item.id,
                                  quantity: 1,
                                  is_free_issued: false,
                              },
                          ],
                      }
            )
        );
        setAddingToIdx(null);
    };

    const applyProcessTemplate = (sIdx: number, template: ProcessTemplate) => {
        const appendedEquipment: StationEquipmentLine[] = template.items.map((item) => ({
            equipment_id: item.equipment_id,
            quantity: item.quantity,
            is_free_issued: item.is_free_issued ?? false,
            from_process_template_id: template.id,
        }));
        const appendedSetupLabor: StationSetupLaborEntry[] = template.setup_labor.map(
            (entry) => ({
                role: entry.role,
                hours: entry.hours,
                worker_type: 'internal',
                from_process_template_id: template.id,
            })
        );
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          equipment: [...(s.equipment ?? []), ...appendedEquipment],
                          setup_labor: [
                              ...(s.setup_labor ?? []),
                              ...appendedSetupLabor,
                          ],
                      }
            )
        );
        setAddingProcessToIdx(null);
    };

    const removeEquipment = (sIdx: number, lIdx: number) => {
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : { ...s, equipment: (s.equipment ?? []).filter((_, j) => j !== lIdx) }
            )
        );
    };

    const removeTemplateContribution = (sIdx: number, templateId: string) => {
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          equipment: (s.equipment ?? []).filter(
                              (l) => l.from_process_template_id !== templateId
                          ),
                          setup_labor: (s.setup_labor ?? []).filter(
                              (e) => e.from_process_template_id !== templateId
                          ),
                      }
            )
        );
    };

    const updateEquipmentLine = (
        sIdx: number,
        lIdx: number,
        updater: (line: StationEquipmentLine) => StationEquipmentLine
    ) => {
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          equipment: (s.equipment ?? []).map((l, j) =>
                              j === lIdx ? updater(l) : l
                          ),
                      }
            )
        );
    };

    const addStationSetupLaborRow = (sIdx: number) => {
        setStations((prev) =>
            prev.map((s, i) => {
                if (i !== sIdx) return s;
                const used = new Set(
                    (s.setup_labor ?? [])
                        .filter((e) => e.from_process_template_id == null)
                        .map((e) => e.role)
                );
                const nextRole = laborRates.find((r) => !used.has(r.role))?.role ?? '';
                return {
                    ...s,
                    setup_labor: [
                        ...(s.setup_labor ?? []),
                        {
                            role: nextRole,
                            hours: 0,
                            worker_type: 'internal',
                            from_process_template_id: null,
                        },
                    ],
                };
            })
        );
    };

    const updateStationSetupLaborRow = (
        sIdx: number,
        entryIdx: number,
        patch: Partial<StationSetupLaborEntry>
    ) => {
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          setup_labor: (s.setup_labor ?? []).map((entry, j) =>
                              j === entryIdx ? { ...entry, ...patch } : entry
                          ),
                      }
            )
        );
    };

    const removeStationSetupLaborRow = (sIdx: number, entryIdx: number) => {
        setStations((prev) =>
            prev.map((s, i) =>
                i !== sIdx
                    ? s
                    : {
                          ...s,
                          setup_labor: (s.setup_labor ?? []).filter(
                              (_, j) => j !== entryIdx
                          ),
                      }
            )
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const saved = await rfqApi.updateConveyorStations(rfqId, stations);
            setStations(saved.map(toEditableConfig));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save conveyor stations');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="mt-6 p-4 border border-[#141414]/15 bg-white/50">
                <div className="ff-mono text-sm text-[#141414]/60">
                    Loading conveyor stations…
                </div>
            </div>
        );
    }

    return (
        <div className="mt-6 grid-bg ff-display text-[#141414] border border-[#141414]/15">
            <header className="border-b border-[#141414]/15 bg-[#f3efe6]/95 backdrop-blur px-6 py-4">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e] flex items-center gap-2">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#e0461e] animate-pulse" />
                            Program RFQ · Configure conveyor
                        </div>
                        <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                            <h3 className="ff-serif text-3xl leading-none">
                                {rfqTitle ?? 'Conveyor configuration'}
                            </h3>
                            <span className="ff-mono text-xs px-2 py-0.5 border border-[#141414]/40 text-[#141414]/70 flex items-center gap-1">
                                Quote Rev{' '}
                                <EditableValue
                                    raw={params.quote_rev ?? ''}
                                    display={formatText(params.quote_rev)}
                                    editable={isAdmin}
                                    onCommit={(raw) =>
                                        persistParams({
                                            quote_rev: raw.trim() || null,
                                        })
                                    }
                                    placeholder="—"
                                    inputClassName="ff-mono text-xs w-20 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                    displayClassName="hover:text-[#e0461e]"
                                />
                            </span>
                            <span className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e] border border-[#e0461e] px-1.5 py-0.5">
                                Draft
                            </span>
                        </div>
                    </div>

                    <div className="flex items-stretch border border-[#141414]/25 bg-white/60">
                        <div className="px-3 py-2">
                            <div className="ff-mono text-[9px] uppercase tracking-[0.18em] text-[#141414]/55">
                                Stations
                            </div>
                            <div className="ff-mono text-sm font-semibold">
                                {stations.length}
                            </div>
                        </div>
                        <div className="px-3 py-2 border-l border-[#141414]/15">
                            <div className="ff-mono text-[9px] uppercase tracking-[0.18em] text-[#141414]/55">
                                Operations
                            </div>
                            <div className="ff-mono text-sm font-semibold">
                                {flatSteps.length}
                            </div>
                        </div>
                        <div className="px-4 py-2 border-l border-[#141414]/15 bg-[#141414] text-[#f3efe6]">
                            <div className="ff-mono text-[9px] uppercase tracking-[0.18em] opacity-60">
                                Line setup
                            </div>
                            <div className="ff-mono text-sm font-bold">
                                {formatCurrency(totals.total)}
                            </div>
                        </div>
                    </div>
                </div>

                {flatSteps.length > 0 && (
                    <div className="mt-3 border border-[#141414]/15 bg-white/40">
                        <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#141414]/10">
                            <span className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/60">
                                Global sequence · fixed
                            </span>
                            <span className="ff-mono text-[10px] text-[#141414]/50">
                                {flatSteps.length} ops
                            </span>
                        </div>
                        <div className="flex items-center overflow-x-auto">
                            {flatSteps.map((op, i) => {
                                const hue = STATION_HUES[op.sIdx % STATION_HUES.length];
                                return (
                                    <div
                                        key={op.id}
                                        className="flex items-center"
                                    >
                                        <div className="px-3 py-2 min-w-[140px]">
                                            <div
                                                className="ff-mono text-[10px]"
                                                style={{ color: hue }}
                                            >
                                                {opCode(op.globalIdx)}
                                            </div>
                                            <div className="text-[12px] leading-tight truncate max-w-[140px]">
                                                {op.text}
                                            </div>
                                        </div>
                                        {i < flatSteps.length - 1 && (
                                            <div className="h-px w-3 bg-[#141414]/20" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </header>

            <section className="px-6 pt-6">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
                        Program specification
                    </div>
                    <div className="flex items-stretch border border-[#141414]/25 bg-white/60">
                        <div className="px-3 py-1.5 flex items-center gap-2">
                            <Truck className="w-3.5 h-3.5 text-[#141414]/60" />
                            <div>
                                <div className="ff-mono text-[9px] uppercase tracking-[0.18em] text-[#141414]/55">
                                    Equipment
                                </div>
                                <div className="ff-mono text-[12px] font-semibold">
                                    <EditableValue
                                        raw={params.equipment_delivery ?? ''}
                                        display={formatText(params.equipment_delivery)}
                                        editable={isAdmin}
                                        type="date"
                                        onCommit={(raw) =>
                                            persistParams({
                                                equipment_delivery: raw.trim() || null,
                                            })
                                        }
                                        inputClassName="ff-mono text-[12px] w-32 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                        displayClassName="hover:text-[#e0461e]"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center text-[#e0461e] px-1 ff-mono">
                            →
                        </div>
                        <div className="px-3 py-1.5 flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 text-[#141414]/60" />
                            <div>
                                <div className="ff-mono text-[9px] uppercase tracking-[0.18em] text-[#141414]/55">
                                    SOP
                                </div>
                                <div className="ff-mono text-[12px] font-semibold">
                                    <EditableValue
                                        raw={params.sop_date ?? ''}
                                        display={formatText(params.sop_date)}
                                        editable={isAdmin}
                                        type="date"
                                        onCommit={(raw) =>
                                            persistParams({
                                                sop_date: raw.trim() || null,
                                            })
                                        }
                                        inputClassName="ff-mono text-[12px] w-32 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                        displayClassName="hover:text-[#e0461e]"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 border border-[#141414]/20 bg-white/50">
                    <Spec
                        icon={<TimerReset className="w-3.5 h-3.5" />}
                        label="TAKT Time"
                        accent
                        value={
                            <EditableValue
                                raw={params.takt_seconds ?? ''}
                                display={formatTakt(params.takt_seconds)}
                                editable={isAdmin}
                                type="number"
                                step={1}
                                min={0}
                                onCommit={(raw) =>
                                    persistParams({
                                        takt_seconds: parseOptionalNumber(raw),
                                    })
                                }
                                inputClassName="num-input ff-mono text-lg font-bold w-20 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                displayClassName="hover:text-[#e0461e]"
                            />
                        }
                        sub={formatTaktRate(params.takt_seconds)}
                    />
                    <Spec
                        icon={<Package className="w-3.5 h-3.5" />}
                        label="Annual Volume"
                        value={
                            <EditableValue
                                raw={params.annual_volume ?? ''}
                                display={formatInt(params.annual_volume)}
                                editable={isAdmin}
                                type="number"
                                step={1}
                                min={0}
                                onCommit={(raw) =>
                                    persistParams({
                                        annual_volume: parseOptionalInt(raw),
                                    })
                                }
                                inputClassName="num-input ff-mono text-lg font-bold w-24 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                displayClassName="hover:text-[#e0461e]"
                            />
                        }
                        sub={
                            <EditableValue
                                raw={params.volume_note ?? ''}
                                display={formatText(params.volume_note)}
                                editable={isAdmin}
                                onCommit={(raw) =>
                                    persistParams({
                                        volume_note: raw.trim() || null,
                                    })
                                }
                                placeholder="units/yr"
                                inputClassName="ff-mono text-[10px] w-24 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                displayClassName="hover:text-[#e0461e]"
                            />
                        }
                    />
                    <Spec
                        icon={<Clock className="w-3.5 h-3.5" />}
                        label="Shifts/day"
                        value={
                            <EditableValue
                                raw={params.shifts_per_day ?? ''}
                                display={formatInt(params.shifts_per_day)}
                                editable={isAdmin}
                                type="number"
                                step={1}
                                min={0}
                                max={10}
                                onCommit={(raw) =>
                                    persistParams({
                                        shifts_per_day: parseOptionalInt(raw),
                                    })
                                }
                                inputClassName="num-input ff-mono text-lg font-bold w-16 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                displayClassName="hover:text-[#e0461e]"
                            />
                        }
                    />
                    <Spec
                        icon={<Clock className="w-3.5 h-3.5" />}
                        label="Hours/shift"
                        value={
                            <EditableValue
                                raw={params.hours_per_shift ?? ''}
                                display={formatInt(params.hours_per_shift)}
                                editable={isAdmin}
                                type="number"
                                step={0.5}
                                min={0}
                                max={24}
                                onCommit={(raw) =>
                                    persistParams({
                                        hours_per_shift: parseOptionalNumber(raw),
                                    })
                                }
                                inputClassName="num-input ff-mono text-lg font-bold w-16 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                displayClassName="hover:text-[#e0461e]"
                            />
                        }
                    />
                    <Spec
                        icon={<Users className="w-3.5 h-3.5" />}
                        label="Operators"
                        value={
                            <EditableValue
                                raw={params.operators ?? ''}
                                display={formatInt(params.operators)}
                                editable={isAdmin}
                                type="number"
                                step={1}
                                min={0}
                                onCommit={(raw) =>
                                    persistParams({
                                        operators: parseOptionalInt(raw),
                                    })
                                }
                                inputClassName="num-input ff-mono text-lg font-bold w-16 px-1 bg-white border border-[#141414]/30 outline-none focus:border-[#e0461e]"
                                displayClassName="hover:text-[#e0461e]"
                            />
                        }
                        sub="per line"
                    />
                </div>

                <div className="mt-3 flex items-stretch border border-[#141414]/20 bg-white/50 flex-wrap">
                    <Stat label="Operations" value={flatSteps.length} mono />
                    <Stat label="Stations" value={stations.length} mono />
                    <Stat label="Equipment" value={totals.eqCount} mono />
                    <Stat label="Capital" value={formatCurrency(totals.goods)} />
                    <Stat label="Setup labor" value={formatCurrency(totals.labor)} />
                    <div className="flex-1 min-w-[140px] px-4 py-2 border-l border-[#141414]/15 bg-[#141414] text-[#f3efe6]">
                        <div className="ff-mono text-[9px] uppercase tracking-[0.18em] opacity-60">
                            Line setup total
                        </div>
                        <div className="ff-mono text-xl font-bold">
                            {formatCurrency(totals.total)}
                        </div>
                    </div>
                </div>
            </section>

            {error && (
                <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                    {error}
                </div>
            )}
            {paramsError && (
                <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                    {paramsError}
                </div>
            )}

            <section className="px-6 pt-6">
                <CycleGantt
                    stations={costed}
                    taktSeconds={params.takt_seconds ?? null}
                    stationHues={STATION_HUES}
                />
            </section>

            <main className="px-6 pt-6 pb-8">
                {stations.length === 0 && (
                    <div className="border border-dashed border-[#141414]/25 px-4 py-10 text-center ff-mono text-sm text-[#141414]/55">
                        No stations yet. Upload a document to extract the sequence of
                        operations.
                    </div>
                )}
                {stations.map((station, sIdx) => {
                    const costedStation = costed[sIdx];
                    const stationOps = flatSteps.filter((f) => f.sIdx === sIdx);
                    return (
                        <div key={sIdx}>
                            <StationCard
                                station={station}
                                index={sIdx}
                                isFirst={sIdx === 0}
                                isLast={sIdx === stations.length - 1}
                                isAdmin={isAdmin}
                                stationOps={stationOps}
                                costed={costedStation}
                                equipmentById={equipmentById}
                                catalog={equipmentCatalog}
                                laborRates={laborRates}
                                processTemplates={processTemplates}
                                templateById={templateById}
                                isAddingOpen={addingToIdx === sIdx}
                                isAddingProcessOpen={addingProcessToIdx === sIdx}
                                onRename={(name) => renameStation(sIdx, name)}
                                onMoveOpPrev={(stepIdx) => moveStepPrev(sIdx, stepIdx)}
                                onMoveOpNext={(stepIdx) => moveStepNext(sIdx, stepIdx)}
                                onSplitAfter={(stepIdx) => splitAfter(sIdx, stepIdx)}
                                onSetStepDuration={(stepIdx, seconds) =>
                                    setStepDuration(sIdx, stepIdx, seconds)
                                }
                                onOpenAdd={() => {
                                    setAddingToIdx(sIdx);
                                    setAddingProcessToIdx(null);
                                }}
                                onCloseAdd={() => setAddingToIdx(null)}
                                onOpenAddProcess={() => {
                                    setAddingProcessToIdx(sIdx);
                                    setAddingToIdx(null);
                                }}
                                onCloseAddProcess={() => setAddingProcessToIdx(null)}
                                onAddEquipment={(item) => addEquipment(sIdx, item)}
                                onApplyProcessTemplate={(template) =>
                                    applyProcessTemplate(sIdx, template)
                                }
                                onRemoveEquipment={(lIdx) => removeEquipment(sIdx, lIdx)}
                                onRemoveTemplateContribution={(templateId) =>
                                    removeTemplateContribution(sIdx, templateId)
                                }
                                onUpdateEquipment={(lIdx, updater) =>
                                    updateEquipmentLine(sIdx, lIdx, updater)
                                }
                                onAddSetupLaborRow={() => addStationSetupLaborRow(sIdx)}
                                onUpdateSetupLaborRow={(idx, patch) =>
                                    updateStationSetupLaborRow(sIdx, idx, patch)
                                }
                                onRemoveSetupLaborRow={(idx) =>
                                    removeStationSetupLaborRow(sIdx, idx)
                                }
                            />
                            {sIdx < stations.length - 1 &&
                                (isAdmin ? (
                                    <StationSeam onMerge={() => mergeWithNext(sIdx)} />
                                ) : (
                                    <div className="my-4 dashed-seam h-px w-full opacity-30" />
                                ))}
                        </div>
                    );
                })}

                <div className="mt-10 flex items-center gap-3 ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/50">
                    <div className="h-px flex-1 bg-[#141414]/15" />
                    <span>end of line</span>
                    <div className="h-px flex-1 bg-[#141414]/15" />
                </div>

                {isAdmin && (
                    <div className="mt-4 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                        >
                            {isSaving ? 'Saving…' : 'Save conveyor stations'}
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
};

const parseOptionalNumber = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? n : null;
};
const parseOptionalInt = (raw: string): number | null => {
    const n = parseOptionalNumber(raw);
    return n == null ? null : Math.round(n);
};

interface EditableValueProps {
    raw: string | number;
    display: React.ReactNode;
    editable: boolean;
    onCommit: (raw: string) => void;
    type?: 'text' | 'number' | 'date';
    step?: number;
    min?: number;
    max?: number;
    placeholder?: string;
    inputClassName?: string;
    displayClassName?: string;
}

const EditableValue: React.FC<EditableValueProps> = ({
    raw,
    display,
    editable,
    onCommit,
    type = 'text',
    step,
    min,
    max,
    placeholder,
    inputClassName,
    displayClassName,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');

    if (!editable) {
        return <>{display}</>;
    }

    if (editing) {
        return (
            <input
                autoFocus
                type={type}
                value={draft}
                step={step}
                min={min}
                max={max}
                placeholder={placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => {
                    onCommit(draft);
                    setEditing(false);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.currentTarget.blur();
                    } else if (e.key === 'Escape') {
                        setEditing(false);
                    }
                }}
                className={inputClassName}
            />
        );
    }

    return (
        <button
            type="button"
            onClick={() => {
                setDraft(raw === null || raw === undefined ? '' : String(raw));
                setEditing(true);
            }}
            title="Click to edit"
            className={`text-left ${displayClassName ?? ''}`}
        >
            {display}
        </button>
    );
};

function Stat({
    label,
    value,
    mono,
    accent,
}: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
    accent?: boolean;
}) {
    return (
        <div className="px-4 py-2 border-l first:border-l-0 border-[#141414]/15 min-w-[90px]">
            <div className="ff-mono text-[9px] uppercase tracking-[0.18em] text-[#141414]/55">
                {label}
            </div>
            <div
                className={`${mono || accent ? 'ff-mono' : 'ff-display'} text-lg ${
                    accent ? 'text-[#e0461e]' : ''
                } font-semibold`}
            >
                {value}
            </div>
        </div>
    );
}

function Spec({
    icon,
    label,
    value,
    sub,
    accent,
}: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    accent?: boolean;
}) {
    return (
        <div className="px-4 py-3 border-l first:border-l-0 border-t sm:border-t-0 first:border-t-0 sm:[&:nth-child(4)]:border-t md:[&:nth-child(4)]:border-t-0 border-[#141414]/15">
            <div className="ff-mono text-[9px] uppercase tracking-[0.22em] text-[#141414]/55 flex items-center gap-1.5">
                {icon}
                {label}
            </div>
            <div
                className={`ff-mono text-lg font-bold leading-tight mt-0.5 ${
                    accent ? 'text-[#e0461e]' : ''
                }`}
            >
                {value}
            </div>
            {sub && (
                <div className="ff-mono text-[10px] text-[#141414]/55 leading-tight">
                    {sub}
                </div>
            )}
        </div>
    );
}

function StationSeam({ onMerge }: { onMerge: () => void }) {
    return (
        <div className="relative my-4 flex items-center justify-center">
            <div className="dashed-seam h-px w-full opacity-40" />
            <button
                type="button"
                onClick={onMerge}
                className="absolute ff-mono text-[10px] uppercase tracking-[0.2em] bg-[#f3efe6] px-3 py-1 border border-[#141414]/25 hover:bg-[#141414] hover:text-[#f3efe6] flex items-center gap-1.5 transition"
            >
                <Link2 className="w-3 h-3" /> merge with next
            </button>
        </div>
    );
}

interface StationCardProps {
    station: ConveyorStationConfig;
    index: number;
    isFirst: boolean;
    isLast: boolean;
    isAdmin: boolean;
    stationOps: FlatOp[];
    costed: ConveyorStationWithCost | undefined;
    equipmentById: Map<string, Equipment>;
    catalog: Equipment[];
    laborRates: LaborRate[];
    processTemplates: ProcessTemplate[];
    templateById: Map<string, ProcessTemplate>;
    isAddingOpen: boolean;
    isAddingProcessOpen: boolean;
    onRename: (name: string) => void;
    onMoveOpPrev: (stepIdx: number) => void;
    onMoveOpNext: (stepIdx: number) => void;
    onSplitAfter: (stepIdx: number) => void;
    onSetStepDuration: (stepIdx: number, seconds: number) => void;
    onOpenAdd: () => void;
    onCloseAdd: () => void;
    onOpenAddProcess: () => void;
    onCloseAddProcess: () => void;
    onAddEquipment: (item: Equipment) => void;
    onApplyProcessTemplate: (template: ProcessTemplate) => void;
    onRemoveEquipment: (lIdx: number) => void;
    onRemoveTemplateContribution: (templateId: string) => void;
    onUpdateEquipment: (
        lIdx: number,
        updater: (l: StationEquipmentLine) => StationEquipmentLine
    ) => void;
    onAddSetupLaborRow: () => void;
    onUpdateSetupLaborRow: (idx: number, patch: Partial<StationSetupLaborEntry>) => void;
    onRemoveSetupLaborRow: (idx: number) => void;
}

const StationCard: React.FC<StationCardProps> = ({
    station,
    index,
    isFirst,
    isLast,
    isAdmin,
    stationOps,
    costed,
    equipmentById,
    catalog,
    laborRates,
    processTemplates,
    templateById,
    isAddingOpen,
    isAddingProcessOpen,
    onRename,
    onMoveOpPrev,
    onMoveOpNext,
    onSplitAfter,
    onSetStepDuration,
    onOpenAdd,
    onCloseAdd,
    onOpenAddProcess,
    onCloseAddProcess,
    onAddEquipment,
    onApplyProcessTemplate,
    onRemoveEquipment,
    onRemoveTemplateContribution,
    onUpdateEquipment,
    onAddSetupLaborRow,
    onUpdateSetupLaborRow,
    onRemoveSetupLaborRow,
}) => {
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState(station.name ?? '');
    const [collapsed, setCollapsed] = useState(false);
    useEffect(() => setNameDraft(station.name ?? ''), [station.name]);

    const stationNum = String(index + 1).padStart(2, '0');
    const goods = costed?.total_goods ?? 0;
    const labor = costed?.total_labor ?? 0;
    const total = costed?.total ?? 0;
    const eqCount = (station.equipment ?? []).length;
    const displayName = station.name ?? `Station ${index + 1}`;

    return (
        <article className="relative bg-white border border-[#141414]/20 shadow-[0_1px_0_0_#141414]">
            <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
            <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
            <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
            <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />

            <div className="flex items-stretch border-b border-[#141414]/15">
                <div className="flex items-center justify-center w-20 bg-[#141414] text-[#f3efe6]">
                    <div className="text-center">
                        <div className="ff-mono text-[9px] uppercase tracking-[0.2em] opacity-60">
                            Station
                        </div>
                        <div className="ff-mono text-2xl font-bold leading-none mt-0.5">
                            {stationNum}
                        </div>
                    </div>
                </div>
                <div className="flex-1 flex items-center justify-between px-5 py-3 gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <Factory className="w-4 h-4 text-[#e0461e]" />
                        {isAdmin && editingName ? (
                            <input
                                autoFocus
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onBlur={() => {
                                    onRename(nameDraft.trim() || displayName);
                                    setEditingName(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                    if (e.key === 'Escape') {
                                        setNameDraft(station.name ?? '');
                                        setEditingName(false);
                                    }
                                }}
                                className="ff-serif text-xl bg-transparent outline-none border-b border-dashed border-[#141414]/40 min-w-0 w-[280px]"
                                aria-label="Station name"
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => isAdmin && setEditingName(true)}
                                className="ff-serif text-xl leading-none hover:text-[#e0461e] transition text-left disabled:opacity-100 disabled:hover:text-current"
                                title={isAdmin ? 'Click to rename' : undefined}
                                disabled={!isAdmin}
                            >
                                {displayName}
                            </button>
                        )}
                    </div>

                    {/* TODO: restore TAKT vs station cycle display once op seconds wire in */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-5 ff-mono text-[11px] text-[#141414]/70 flex-wrap">
                            <span className="flex items-center gap-1.5">
                                <Wrench className="w-3 h-3" /> {eqCount} eq
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Coins className="w-3 h-3" /> {formatCurrency(goods)}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Users className="w-3 h-3" /> {formatCurrency(labor)}
                            </span>
                            <span className="flex items-center gap-1.5 text-[#e0461e] font-semibold">
                                = {formatCurrency(total)}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setCollapsed((c) => !c)}
                            aria-expanded={!collapsed}
                            title={collapsed ? 'Expand station' : 'Collapse station'}
                            className="p-1 border border-[#141414]/25 bg-white/60 hover:bg-[#141414] hover:text-[#f3efe6] transition"
                        >
                            <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                    collapsed ? '-rotate-90' : ''
                                }`}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {!collapsed && (
            <>
            <div className="px-5 pt-5">
                <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/50 mb-2">
                    Operations ({stationOps.length})
                </div>
                {stationOps.length === 0 ? (
                    <div className="border border-dashed border-[#141414]/25 px-4 py-4 text-center ff-mono text-xs text-[#141414]/50">
                        No operations in this station.
                    </div>
                ) : (
                    <div className="flex flex-wrap items-stretch gap-0">
                        {stationOps.map((op, i) => {
                            const isFirstOp = i === 0;
                            const isLastOp = i === stationOps.length - 1;
                            return (
                                <div
                                    key={op.id}
                                    className="flex items-stretch group"
                                >
                                    <div className="relative border border-[#141414]/20 bg-[#f8f5ec] px-3 py-2 min-w-[180px] max-w-[260px] flex flex-col justify-between">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="ff-mono text-[10px] text-[#e0461e] flex items-center justify-between gap-2">
                                                    <span>{opCode(op.globalIdx)}</span>
                                                    {isAdmin ? (
                                                        <label className="flex items-center gap-1 text-[#141414]/55">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                step={0.5}
                                                                value={op.durationSeconds}
                                                                onChange={(e) =>
                                                                    onSetStepDuration(
                                                                        op.stepIdx,
                                                                        Number(e.target.value)
                                                                    )
                                                                }
                                                                className="num-input ff-mono text-[10px] w-12 px-1 py-0 text-right bg-white border border-[#141414]/25 outline-none focus:border-[#e0461e]"
                                                                aria-label="Duration (seconds)"
                                                            />
                                                            <span>s</span>
                                                        </label>
                                                    ) : (
                                                        <span className="text-[#141414]/55">
                                                            {op.durationSeconds}s
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[13px] leading-tight">
                                                    {op.text}
                                                </div>
                                            </div>
                                        </div>
                                        {isAdmin && (
                                            <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                                                {isFirstOp && !isFirst && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onMoveOpPrev(op.stepIdx)
                                                        }
                                                        title="Move to previous station"
                                                        className="h-5 px-1.5 ff-mono text-[9px] uppercase tracking-wider bg-white border border-[#141414]/25 hover:bg-[#141414] hover:text-[#f3efe6] flex items-center gap-1"
                                                    >
                                                        <ArrowUp className="w-2.5 h-2.5" />{' '}
                                                        prev
                                                    </button>
                                                )}
                                                {isLastOp && !isLast && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onMoveOpNext(op.stepIdx)
                                                        }
                                                        title="Move to next station"
                                                        className="h-5 px-1.5 ff-mono text-[9px] uppercase tracking-wider bg-white border border-[#141414]/25 hover:bg-[#141414] hover:text-[#f3efe6] flex items-center gap-1"
                                                    >
                                                        <ArrowDown className="w-2.5 h-2.5" />{' '}
                                                        next
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {!isLastOp && isAdmin && (
                                        <button
                                            type="button"
                                            onClick={() => onSplitAfter(op.stepIdx)}
                                            title="Split station here"
                                            aria-label="Split station here"
                                            className="relative w-5 flex items-center justify-center group/split hover:bg-[#e0461e]/10"
                                        >
                                            <div className="h-full w-px bg-[#141414]/10 group-hover/split:bg-[#e0461e]" />
                                            <Scissors className="w-3 h-3 absolute text-[#141414]/0 group-hover/split:text-[#e0461e] transition" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="px-5 pt-6 pb-5">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/50">
                        Equipment ({eqCount})
                    </div>
                    {isAdmin && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={onOpenAddProcess}
                                disabled={processTemplates.length === 0}
                                title={
                                    processTemplates.length === 0
                                        ? 'No process templates defined yet'
                                        : undefined
                                }
                                className="ff-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#141414] hover:bg-[#141414] hover:text-[#f3efe6] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#141414] flex items-center gap-1.5 transition"
                            >
                                <Plus className="w-3 h-3" /> Add process
                            </button>
                            <button
                                type="button"
                                onClick={onOpenAdd}
                                className="ff-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#141414] hover:bg-[#141414] hover:text-[#f3efe6] flex items-center gap-1.5 transition"
                            >
                                <Plus className="w-3 h-3" /> Add equipment
                            </button>
                        </div>
                    )}
                </div>

                <div className="hidden md:grid grid-cols-12 gap-3 ff-mono text-[9px] uppercase tracking-[0.2em] text-[#141414]/45 border-b border-[#141414]/10 pb-1.5">
                    <div className="col-span-6">Item</div>
                    <div className="col-span-2 text-right">Qty</div>
                    <div className="col-span-2 text-right">Capital</div>
                    <div className="col-span-2 text-right">Line total</div>
                </div>

                {eqCount === 0 && !isAddingOpen && (
                    <div className="border border-dashed border-[#141414]/25 px-4 py-6 text-center ff-mono text-xs text-[#141414]/50">
                        No equipment yet. Add from catalog to build up the station's
                        setup cost.
                    </div>
                )}

                <div className="divide-y divide-[#141414]/10">
                    {(station.equipment ?? []).map((line, lIdx) => {
                        const costedLine = costed?.equipment[lIdx];
                        const item = equipmentById.get(line.equipment_id);
                        const tag = line.from_process_template_id ?? null;
                        const prevTag =
                            lIdx > 0
                                ? (station.equipment?.[lIdx - 1].from_process_template_id ?? null)
                                : null;
                        const showGroupHeader = tag !== null && tag !== prevTag;
                        const templateName = tag ? templateById.get(tag)?.name ?? tag : null;
                        return (
                            <React.Fragment key={lIdx}>
                                {showGroupHeader && (
                                    <div className="py-1.5 flex items-center justify-between gap-2">
                                        <span className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
                                            From process:{' '}
                                            <span className="text-[#e0461e]">
                                                {templateName}
                                            </span>
                                        </span>
                                        {isAdmin && tag && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onRemoveTemplateContribution(tag)
                                                }
                                                className="ff-mono text-[9px] uppercase tracking-wider text-[#141414]/55 hover:text-[#c2251a] transition"
                                            >
                                                remove process
                                            </button>
                                        )}
                                    </div>
                                )}
                                <EquipmentRow
                                    line={line}
                                    costed={costedLine}
                                    item={item}
                                    isAdmin={isAdmin}
                                    onRemove={() => onRemoveEquipment(lIdx)}
                                    onUpdate={(updater) => onUpdateEquipment(lIdx, updater)}
                                />
                            </React.Fragment>
                        );
                    })}
                </div>

                {isAddingProcessOpen && (
                    <div className="mt-3 border border-[#141414] bg-[#f8f5ec]">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-[#141414]/20">
                            <span className="ff-mono text-[10px] uppercase tracking-[0.2em]">
                                Process templates
                            </span>
                            <button
                                type="button"
                                onClick={onCloseAddProcess}
                                className="p-1 hover:bg-[#141414]/10"
                                aria-label="Close process templates"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        {processTemplates.length === 0 ? (
                            <div className="px-3 py-4 ff-mono text-xs text-[#141414]/50">
                                No process templates defined yet. Create one in
                                Inventory · Process templates.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#141414]/10">
                                {processTemplates.map((template) => {
                                    const unitCount = template.items.reduce(
                                        (acc, it) => acc + it.quantity,
                                        0
                                    );
                                    return (
                                        <button
                                            key={template.id}
                                            type="button"
                                            onClick={() => onApplyProcessTemplate(template)}
                                            className="text-left px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-[#e0461e]/10"
                                        >
                                            <div className="min-w-0">
                                                <div className="text-[13px] truncate">
                                                    {template.name}
                                                </div>
                                                <div className="ff-mono text-[10px] text-[#141414]/55">
                                                    {template.items.length} item
                                                    {template.items.length === 1 ? '' : 's'} ·{' '}
                                                    {unitCount} unit
                                                    {unitCount === 1 ? '' : 's'}
                                                    {template.category
                                                        ? ` · ${template.category}`
                                                        : ''}
                                                </div>
                                            </div>
                                            <Plus className="w-4 h-4 text-[#e0461e] shrink-0" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {isAddingOpen && (
                    <div className="mt-3 border border-[#141414] bg-[#f8f5ec]">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-[#141414]/20">
                            <span className="ff-mono text-[10px] uppercase tracking-[0.2em]">
                                Equipment catalog
                            </span>
                            <button
                                type="button"
                                onClick={onCloseAdd}
                                className="p-1 hover:bg-[#141414]/10"
                                aria-label="Close catalog"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        {catalog.length === 0 ? (
                            <div className="px-3 py-4 ff-mono text-xs text-[#141414]/50">
                                No equipment available in catalog.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#141414]/10">
                                {catalog.map((item) => {
                                    const already = (station.equipment ?? []).some(
                                        (e) => e.equipment_id === item.id
                                    );
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() =>
                                                !already && onAddEquipment(item)
                                            }
                                            disabled={already}
                                            className={`text-left px-3 py-2.5 flex items-center justify-between gap-2 ${
                                                already
                                                    ? 'opacity-40 cursor-not-allowed'
                                                    : 'hover:bg-[#e0461e]/10'
                                            }`}
                                        >
                                            <div>
                                                <div className="text-[13px]">
                                                    {item.name}
                                                </div>
                                                <div className="ff-mono text-[10px] text-[#141414]/55">
                                                    capital {formatCurrency(item.unit_cost)}
                                                    {item.category
                                                        ? ` · ${item.category}`
                                                        : ''}
                                                </div>
                                            </div>
                                            {already ? (
                                                <CheckCircle2 className="w-4 h-4 text-[#141414]/30" />
                                            ) : (
                                                <Plus className="w-4 h-4 text-[#e0461e]" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-5 border-t border-[#141414]/15 pt-4">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/50">
                            Setup labor ({(station.setup_labor ?? []).length})
                        </div>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={onAddSetupLaborRow}
                                disabled={laborRates.length === 0}
                                className="ff-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#141414] hover:bg-[#141414] hover:text-[#f3efe6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition"
                            >
                                <Plus className="w-3 h-3" /> Add role
                            </button>
                        )}
                    </div>

                    {(station.setup_labor ?? []).length === 0 ? (
                        <div className="border border-dashed border-[#141414]/25 px-4 py-4 text-center ff-mono text-xs text-[#141414]/50">
                            No setup labor yet. Add roles or apply a process to charge
                            setup hours.
                        </div>
                    ) : (
                        <div className="divide-y divide-[#141414]/10">
                            {(station.setup_labor ?? []).map((entry, eIdx) => (
                                <SetupLaborRow
                                    key={`${entry.role}-${entry.from_process_template_id ?? ''}-${eIdx}`}
                                    entry={entry}
                                    costed={costed?.setup_labor[eIdx]}
                                    laborRates={laborRates}
                                    isAdmin={isAdmin}
                                    onUpdate={(patch) =>
                                        onUpdateSetupLaborRow(eIdx, patch)
                                    }
                                    onRemove={() => onRemoveSetupLaborRow(eIdx)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-4 flex items-center justify-between border-t-2 border-[#141414] pt-2.5 flex-wrap gap-3">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
                        Station {stationNum} setup cost
                    </div>
                    <div className="flex items-center gap-6 ff-mono text-sm">
                        <span>
                            <span className="text-[#141414]/50 text-[10px] uppercase tracking-wider mr-2">
                                Capital
                            </span>
                            <span className="font-semibold">{formatCurrency(goods)}</span>
                        </span>
                        <span>
                            <span className="text-[#141414]/50 text-[10px] uppercase tracking-wider mr-2">
                                Labor
                            </span>
                            <span className="font-semibold">{formatCurrency(labor)}</span>
                        </span>
                        <span className="pl-6 border-l border-[#141414]/20">
                            <span className="text-[#141414]/50 text-[10px] uppercase tracking-wider mr-2">
                                Total
                            </span>
                            <span className="font-bold text-[#e0461e] text-base">
                                {formatCurrency(total)}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
            </>
            )}
        </article>
    );
};

interface EquipmentRowProps {
    line: StationEquipmentLine;
    costed: StationEquipmentLineCost | undefined;
    item: Equipment | undefined;
    isAdmin: boolean;
    onRemove: () => void;
    onUpdate: (updater: (l: StationEquipmentLine) => StationEquipmentLine) => void;
}

const EquipmentRow: React.FC<EquipmentRowProps> = ({
    line,
    costed,
    item,
    isAdmin,
    onRemove,
    onUpdate,
}) => {
    const goods = costed?.goods ?? 0;
    const total = costed?.total ?? 0;
    const name = costed?.equipment_name ?? item?.name ?? line.equipment_id;

    return (
        <div className="py-2.5">
            <div className="grid grid-cols-12 gap-3 items-center group">
                <div className="col-span-12 md:col-span-6 flex items-center gap-2">
                    <div className="h-6 w-6 border border-[#141414]/25 flex items-center justify-center bg-[#f8f5ec]">
                        <Wrench className="w-3 h-3" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[14px] truncate">{name}</div>
                        {line.is_free_issued && (
                            <div className="ff-mono text-[9px] uppercase tracking-wider text-[#b87a1a]">
                                free-issued
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-span-4 md:col-span-2 flex items-center justify-end gap-1">
                    {isAdmin ? (
                        <input
                            type="number"
                            min={1}
                            value={line.quantity}
                            onChange={(e) => {
                                const qty = Math.max(1, Number(e.target.value) || 1);
                                onUpdate((l) => ({ ...l, quantity: qty }));
                            }}
                            className="num-input ff-mono text-sm w-14 px-1 py-1 text-right bg-white border border-[#141414]/25 outline-none focus:border-[#e0461e]"
                            aria-label="Quantity"
                        />
                    ) : (
                        <span className="ff-mono text-sm">×{line.quantity}</span>
                    )}
                </div>

                <div className="col-span-4 md:col-span-2 text-right ff-mono text-sm">
                    <span className="md:hidden ff-mono text-[9px] uppercase tracking-wider text-[#141414]/50 mr-1">
                        Capital
                    </span>
                    {formatCurrency(goods)}
                </div>

                <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-2">
                    <span className="ff-mono text-sm font-semibold">
                        {formatCurrency(total)}
                    </span>
                    {isAdmin && (
                        <>
                            <button
                                type="button"
                                onClick={() =>
                                    onUpdate((l) => ({
                                        ...l,
                                        is_free_issued: !(l.is_free_issued ?? false),
                                    }))
                                }
                                className={`ff-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border transition ${
                                    line.is_free_issued
                                        ? 'bg-[#b87a1a] text-[#f3efe6] border-[#b87a1a]'
                                        : 'border-[#141414]/25 text-[#141414]/60 hover:border-[#141414]'
                                }`}
                                title="Toggle free-issued (capital cost = 0)"
                            >
                                free
                            </button>
                            <button
                                type="button"
                                onClick={onRemove}
                                title="Remove"
                                aria-label="Remove equipment"
                                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[#141414]/10 transition"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

interface SetupLaborRowProps {
    entry: StationSetupLaborEntry;
    costed: StationSetupLaborCost | undefined;
    laborRates: LaborRate[];
    isAdmin: boolean;
    onUpdate: (patch: Partial<StationSetupLaborEntry>) => void;
    onRemove: () => void;
}

const SetupLaborRow: React.FC<SetupLaborRowProps> = ({
    entry,
    costed,
    laborRates,
    isAdmin,
    onUpdate,
    onRemove,
}) => {
    const tagged = entry.from_process_template_id != null;
    const editableRoleHours = isAdmin && !tagged;
    const rate = costed?.rate ?? 0;
    const amount = costed?.amount ?? 0;
    const displayName =
        costed?.role_name
        ?? laborRates.find((r) => r.role === entry.role)?.name
        ?? entry.role;
    return (
        <div className="py-2 grid grid-cols-12 gap-3 items-center group">
            <div className="col-span-6 md:col-span-4 flex items-center gap-2">
                <div className="h-6 w-6 border border-[#141414]/25 flex items-center justify-center bg-[#f8f5ec]">
                    <Users className="w-3 h-3" />
                </div>
                {editableRoleHours ? (
                    <select
                        value={entry.role}
                        onChange={(e) => onUpdate({ role: e.target.value })}
                        className="bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-sm"
                        aria-label="Role"
                    >
                        {laborRates.map((r) => (
                            <option key={r.role} value={r.role}>
                                {r.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <div className="min-w-0">
                        <div className="ff-mono text-sm">{displayName}</div>
                        {tagged && (
                            <div className="ff-mono text-[9px] uppercase tracking-wider text-[#141414]/55">
                                from {entry.from_process_template_id}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="col-span-3 md:col-span-2 flex items-center gap-1">
                {editableRoleHours ? (
                    <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={entry.hours}
                        onChange={(e) => {
                            const next = Number(e.target.value);
                            onUpdate({
                                hours: Number.isFinite(next) && next >= 0 ? next : 0,
                            });
                        }}
                        className="num-input ff-mono text-sm w-20 px-1 py-1 text-right bg-white border border-[#141414]/25 outline-none focus:border-[#e0461e]"
                        aria-label="Hours"
                    />
                ) : (
                    <span className="ff-mono text-sm">{entry.hours}</span>
                )}
                <span className="ff-mono text-[10px] text-[#141414]/55">hrs</span>
            </div>
            <div className="col-span-3 md:col-span-2">
                {isAdmin ? (
                    <div
                        className="inline-flex border border-[#141414]/25"
                        role="group"
                        aria-label="Worker type"
                    >
                        <WorkerTypeButton
                            label="int"
                            value="internal"
                            selected={entry.worker_type === 'internal'}
                            onClick={() => onUpdate({ worker_type: 'internal' })}
                        />
                        <WorkerTypeButton
                            label="ext"
                            value="external"
                            selected={entry.worker_type === 'external'}
                            onClick={() => onUpdate({ worker_type: 'external' })}
                        />
                    </div>
                ) : (
                    <span className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/70">
                        {entry.worker_type === 'external' ? 'external' : 'internal'}
                    </span>
                )}
            </div>
            <div className="col-span-3 md:col-span-1 text-right ff-mono text-xs text-[#141414]/70">
                {formatCurrency(rate)}/hr
            </div>
            <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-2">
                <span className="ff-mono text-sm font-semibold">
                    {formatCurrency(amount)}
                </span>
                {editableRoleHours && (
                    <button
                        type="button"
                        onClick={onRemove}
                        title="Remove"
                        aria-label="Remove setup labor role"
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[#141414]/10 transition"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
};

interface WorkerTypeButtonProps {
    label: string;
    value: 'internal' | 'external';
    selected: boolean;
    onClick: () => void;
}

const WorkerTypeButton: React.FC<WorkerTypeButtonProps> = ({
    label,
    value,
    selected,
    onClick,
}) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        aria-label={value}
        className={`ff-mono text-[10px] uppercase tracking-wider px-2 py-1 transition ${
            selected
                ? 'bg-[#141414] text-[#f3efe6]'
                : 'bg-white text-[#141414]/60 hover:text-[#141414]'
        }`}
    >
        {label}
    </button>
);

export default ConveyorStationsConfig;
